import { useMemo, useState } from "react";
import {
  AudiencesClient,
  PartiesClient,
  type NodeSummary,
  type PartySummary,
} from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync } from "../components/ui";

const audiences = new AudiencesClient("");
const parties = new PartiesClient("");

/**
 * Console module: <b>Hierarchy</b>.
 *
 * "Ontario stores" is usually "everything under the Ontario node". Flat attributes fake it until
 * someone asks for Eastern Canada — Ontario *and* Quebec — at which point every store needs a
 * second hand-maintained tag that drifts (§4.4).
 *
 * It stays optional per tenant: a forty-store client with no org structure uses attributes and
 * never opens this screen. The empty state says so rather than implying something is missing.
 */
export function HierarchyPage() {
  const { hasCapability } = usePermissions();
  const nodes = useAsync(() => audiences.audiences_ListNodes());
  const partyList = useAsync(() => parties.parties_List());

  const [editing, setEditing] = useState<NodeSummary | null>(null);
  const [adding, setAdding] = useState<{ parentKey: string | null } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canAdmin = hasCapability("audiences:admin");
  const canAttach = hasCapability("parties:write");

  const tree = useMemo(() => layOut(nodes.data ?? []), [nodes.data]);
  const selectedNode = (nodes.data ?? []).find((n) => n.key === selected) ?? null;

  async function remove(key: string) {
    setActionError(null);
    try {
      await audiences.audiences_DeleteNode(key);
      setSelected(null);
      setEditing(null);
      await nodes.reload();
    } catch (e) {
      setActionError(describeError(e));
    }
  }

  async function refreshBoth() {
    await Promise.all([nodes.reload(), partyList.reload()]);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Hierarchy</h1>
          <p className="sub">
            An optional tree of your own nodes — region, district, site, or whatever you use. A
            party attached to a node is also matched by every node above it.
          </p>
        </div>
        {canAdmin && (nodes.data ?? []).length > 0 && (
          <button onClick={() => { setAdding({ parentKey: null }); setEditing(null); }}>Add a root node</button>
        )}
      </div>

      {nodes.error && <Alert kind="error">{nodes.error}</Alert>}
      {partyList.error && <Alert kind="error">{partyList.error}</Alert>}
      {actionError && <Alert kind="error">{actionError}</Alert>}

      {(editing || adding) && canAdmin && (
        <NodeEditor
          key={editing?.key ?? `new-under-${adding?.parentKey ?? "root"}`}
          node={editing}
          parentKey={adding?.parentKey ?? null}
          nodes={nodes.data ?? []}
          onDone={() => { setEditing(null); setAdding(null); void nodes.reload(); }}
          onCancel={() => { setEditing(null); setAdding(null); }}
        />
      )}

      {nodes.loading ? (
        <Panel><Empty>Loading…</Empty></Panel>
      ) : (nodes.data ?? []).length === 0 ? (
        <Panel title="No hierarchy">
          <p className="muted" style={{ maxWidth: "70ch" }}>
            A hierarchy is optional. Attributes such as <span className="mono">region = ON</span>{" "}
            already address most audiences without one.
          </p>
          <p className="muted" style={{ maxWidth: "70ch" }}>
            Add one if you need groups of groups — for example Eastern Canada meaning Ontario and
            Quebec together.
          </p>
          {canAdmin && (
            <button onClick={() => setAdding({ parentKey: null })}>Start a hierarchy</button>
          )}
        </Panel>
      ) : (
        <div className="grid-2">
          <Panel title="Tree">
            <table>
              <thead>
                <tr><th>Node</th><th>Parties</th><th></th></tr>
              </thead>
              <tbody>
                {tree.map(({ node, indent, isLast }) => (
                  <tr
                    key={node.key}
                    style={{
                      background: selected === node.key ? "var(--panel-2)" : undefined,
                      cursor: "pointer",
                    }}
                    onClick={() => setSelected(node.key!)}
                  >
                    <td>
                      <span className="mono muted">
                        {indent > 0 ? `${"   ".repeat(indent - 1)}${isLast ? "└─ " : "├─ "}` : ""}
                      </span>
                      <strong>{node.name}</strong>{" "}
                      <span className="mono muted">{node.key}</span>
                    </td>
                    <td>{node.partyCount || <span className="muted">—</span>}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {canAdmin && (
                        <>
                          <button
                            className="ghost small"
                            onClick={(e) => { e.stopPropagation(); setEditing(node); setAdding(null); }}
                          >
                            Edit
                          </button>{" "}
                          <button
                            className="ghost small"
                            onClick={(e) => { e.stopPropagation(); setAdding({ parentKey: node.key! }); setEditing(null); }}
                          >
                            Add under
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
              Moving a node also moves everything beneath it. Audience membership updates
              immediately.
            </p>
          </Panel>

          <NodeDetail
            node={selectedNode}
            parties={partyList.data ?? []}
            canAttach={canAttach}
            canAdmin={canAdmin}
            onChanged={refreshBoth}
            onDelete={remove}
            onError={setActionError}
          />
        </div>
      )}
    </>
  );
}

function NodeDetail({ node, parties: allParties, canAttach, canAdmin, onChanged, onDelete, onError }: {
  node: NodeSummary | null;
  parties: PartySummary[];
  canAttach: boolean;
  canAdmin: boolean;
  onChanged: () => Promise<void>;
  onDelete: (key: string) => void;
  onError: (message: string | null) => void;
}) {
  const [attaching, setAttaching] = useState("");
  const [busy, setBusy] = useState(false);

  if (!node) {
    return (
      <Panel title="Node">
        <Empty>Select a node to see and change which parties are attached to it.</Empty>
      </Panel>
    );
  }

  const attached = allParties.filter((p) => p.nodeKey === node.key);
  const unattached = allParties.filter((p) => p.nodeKey !== node.key);

  async function move(partyKey: string, nodeKey: string | null) {
    setBusy(true);
    onError(null);
    try {
      await parties.parties_SetNode(partyKey, { nodeKey: nodeKey ?? undefined });
      setAttaching("");
      await onChanged();
    } catch (e) {
      onError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={`${node.name} · ${node.key}`}
      actions={
        canAdmin && (
          <button className="danger small" onClick={() => onDelete(node.key!)}>Remove node</button>
        )
      }
    >
      <div className="row" style={{ marginBottom: 14, gap: 18 }}>
        <div>
          <div className="muted" style={{ fontSize: 11.5 }}>PARENT</div>
          <div className="mono">{node.parentKey ?? <span className="muted">root</span>}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11.5 }}>DEPTH</div>
          <div>{node.depth}</div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11.5 }}>NODES BENEATH</div>
          <div>{node.childCount}</div>
        </div>
      </div>

      <p className="muted" style={{ fontSize: 12 }}>
        Addressed as <span className="mono">{`{ under: "${node.key}" }`}</span> — reaches every
        party attached here and beneath.
      </p>

      {canAttach && unattached.length > 0 && (
        <div className="row" style={{ marginBottom: 12 }}>
          <Field label="Attach a party">
            <select value={attaching} onChange={(e) => setAttaching(e.target.value)}>
              <option value="">Choose…</option>
              {unattached.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.key} — {p.name}{p.nodeKey ? ` (now under ${p.nodeKey})` : ""}
                </option>
              ))}
            </select>
          </Field>
          <button disabled={busy || !attaching} onClick={() => move(attaching, node.key!)}>Attach</button>
        </div>
      )}

      {attached.length === 0 ? (
        <Empty>No parties attached. Attach one here or to a node beneath it.</Empty>
      ) : (
        <table>
          <thead><tr><th>Party</th><th>Type</th><th>Channels</th><th></th></tr></thead>
          <tbody>
            {attached.map((p) => (
              <tr key={p.key}>
                <td>
                  <span className="mono">{p.key}</span>
                  <div className="muted">{p.name}</div>
                </td>
                <td>{p.type}</td>
                <td>{(p.channels ?? []).map((c) => <Chip key={c}>{c}</Chip>)}</td>
                <td>
                  {canAttach && (
                    <button className="ghost small" disabled={busy} onClick={() => move(p.key!, null)}>
                      Detach
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function NodeEditor({ node, parentKey, nodes, onDone, onCancel }: {
  node: NodeSummary | null;
  parentKey: string | null;
  nodes: NodeSummary[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [key, setKey] = useState(node?.key ?? "");
  const [name, setName] = useState(node?.name ?? "");
  const [parent, setParent] = useState(node ? (node.parentKey ?? "") : (parentKey ?? ""));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A node cannot be moved under itself. Deeper cycles are refused by the API, which rebuilds the
  // ancestor index and rolls the move back — this only keeps the obvious case off the screen.
  const parentChoices = nodes.filter((n) => n.key !== node?.key);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await audiences.audiences_UpsertNode(key, { name, parentKey: parent || undefined });
      onDone();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={node ? `Edit node — ${node.key}` : parentKey ? `Add a node under ${parentKey}` : "Add a root node"}
      actions={<button className="ghost small" onClick={onCancel}>Cancel</button>}
    >
      {error && <Alert kind="error">{error}</Alert>}
      <div className="row">
        <Field label="Key">
          <input value={key} onChange={(e) => setKey(e.target.value)} disabled={!!node} placeholder="n_ontario" />
        </Field>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ontario" />
        </Field>
        <Field label="Parent">
          <select value={parent} onChange={(e) => setParent(e.target.value)}>
            <option value="">(root)</option>
            {parentChoices.map((n) => (
              <option key={n.key} value={n.key}>{`${"— ".repeat(n.depth ?? 0)}${n.name} (${n.key})`}</option>
            ))}
          </select>
        </Field>
        <button onClick={submit} disabled={busy || !key || !name}>{node ? "Save" : "Create"}</button>
      </div>
      {node && (
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          Changing the parent moves this node and everything beneath it. Audience membership
          updates immediately.
        </p>
      )}
    </Panel>
  );
}

/** Depth-first order with the indent guides, from the flat list the API returns. */
function layOut(nodes: NodeSummary[]): { node: NodeSummary; indent: number; isLast: boolean }[] {
  const childrenOf = (parentKey: string | null) =>
    nodes
      .filter((n) => (n.parentKey ?? null) === parentKey)
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

  const out: { node: NodeSummary; indent: number; isLast: boolean }[] = [];

  const walk = (parentKey: string | null, indent: number) => {
    const siblings = childrenOf(parentKey);
    siblings.forEach((node, i) => {
      out.push({ node, indent, isLast: i === siblings.length - 1 });
      walk(node.key!, indent + 1);
    });
  };

  walk(null, 0);

  // Anything the walk did not reach is an orphan — a node whose parent is missing. It should not
  // happen, and showing it is better than hiding it.
  for (const node of nodes) {
    if (!out.some((row) => row.node.key === node.key)) {
      out.push({ node, indent: 0, isLast: true });
    }
  }

  return out;
}
